import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Formik, Form, ErrorMessage } from 'formik';
import * as Yup from 'yup';

import { BaseLayout, SurveyThankYou, Spinner } from '@/components';
import {
  Card,
  CardContent,
  Button,
  Label,
  Input,
  Textarea,
} from '@/components/ui';
import { TOAST_TYPES } from '@/constants/common.constants';
import { useToast } from '@/contexts/toast-context';
import { createCustomerFeedback } from '@/api/customer-feedback.api';

import './customer-feedbacks-add-style.scss';

export const Route = createFileRoute('/customer-feedbacks/add/')({
  component: CustomerFeedbackCreate,
});

function CustomerFeedbackCreate() {
  const { addToast } = useToast();
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Formik values and schema
  interface CustomerFeedbackFormValues {
    customerName: string;
    customerFeedback: string;
  }

  const initialValues: CustomerFeedbackFormValues = {
    customerName: '',
    customerFeedback: '',
  };

  const validationSchema = Yup.object().shape({
    customerName: Yup.string().trim().required('Customer name is required'),
    customerFeedback: Yup.string()
      .trim()
      .required('Customer feedback is required'),
  });

  const handleSubmit = async (values: CustomerFeedbackFormValues) => {
    try {
      const response = await createCustomerFeedback({
        customerName: values.customerName.trim(),
        customerFeedback: values.customerFeedback.trim(),
      });
      addToast(response.message || 'Customer feedback submitted successfully', {
        type: TOAST_TYPES.SUCCESS,
      });
      setIsSubmitted(true);
      window.scrollTo(0, 0);
    } catch (err) {
      addToast(
        err instanceof Error
          ? err.message
          : 'Failed to submit customer feedback',
        { type: TOAST_TYPES.ERROR },
      );
    }
  };

  if (isSubmitted) {
    return (
      <BaseLayout header={{ headerText: 'Customer Feedback' }}>
        <SurveyThankYou message="Your customer feedback has been submitted successfully. It will be routed to your manager for review." />
      </BaseLayout>
    );
  }

  return (
    <BaseLayout header={{ headerText: 'Customer Feedback' }}>
      <Card className="customer-feedback-add-card">
        <CardContent>
          <h2 className="customer-feedback-add-heading">
            Submit your customer feedback below
          </h2>

          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({
              values,
              setFieldValue,
              setFieldTouched,
              isSubmitting,
              errors,
              touched,
            }) => (
              <Form>
                <div className="form-container">
                  <div>
                    <Label htmlFor="customerName">
                      Customer Name<sup>*</sup>
                    </Label>
                    <Input
                      id="customerName"
                      name="customerName"
                      value={values.customerName}
                      onChange={(e) =>
                        setFieldValue('customerName', e.target.value)
                      }
                      onBlur={() => setFieldTouched('customerName', true)}
                      placeholder="Enter customer name"
                      className={
                        errors.customerName && touched.customerName
                          ? 'is-invalid'
                          : ''
                      }
                    />
                    <ErrorMessage
                      name="customerName"
                      component="div"
                      className="invalid-feedback"
                    />
                  </div>

                  <div>
                    <Label htmlFor="customerFeedback">
                      Customer feedback or information<sup>*</sup>
                    </Label>
                    <Textarea
                      id="customerFeedback"
                      name="customerFeedback"
                      value={values.customerFeedback}
                      onChange={(e) =>
                        setFieldValue('customerFeedback', e.target.value)
                      }
                      onBlur={() => setFieldTouched('customerFeedback', true)}
                      placeholder="Enter customer feedback or information"
                      rows={6}
                      className={
                        errors.customerFeedback && touched.customerFeedback
                          ? 'is-invalid'
                          : ''
                      }
                    />
                    <ErrorMessage
                      name="customerFeedback"
                      component="div"
                      className="invalid-feedback"
                    />
                  </div>

                  <div className="button-container-right">
                    <Button
                      type="submit"
                      variant="basic-primary"
                      size="medium"
                      disabled={isSubmitting}
                    >
                      {isSubmitting && <Spinner size="small" />}
                      Submit
                    </Button>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </CardContent>
      </Card>
    </BaseLayout>
  );
}
